/**
 * useReferenceMarkData.js
 *
 * Unified hook that replaces useYReferences, useClaimAnalysis, and
 * useSuggestedReferences with a single Y.Map observer per reference mark.
 *
 * Uses `useSyncExternalStore` for tear-free reads and minimal re-renders.
 * A version counter ensures `getSnapshot` only rebuilds the snapshot when
 * the observer actually fires, keeping the returned object referentially
 * stable between renders.
 *
 * @example
 *   // Full data (header + all tabs)
 *   const { references, claims, meta, summary, suggestedReferences } =
 *       useReferenceMarkData(marker.uid);
 *
 *   // Only references (for count in collapsed header)
 *   const { references } = useReferenceMarkData(marker.uid, { fields: ['references'] });
 */

import {useCallback, useRef, useSyncExternalStore} from 'react';
import useYDocStore from '../stores/useYDocStore';
import {getReferencesForMark, excludeForecastReferences} from '../components/prosemirror-editor/yMaps/referencesYMap';
import {getClaimsForMark, getSuggestedReferences} from '../components/prosemirror-editor/yMaps/claimsYMap';
import {subscribeToMark} from '../components/prosemirror-editor/yMaps/observeMarkRegistry';

// ---------------------------------------------------------------------------
// Snapshot builder
// ---------------------------------------------------------------------------

const EMPTY_REFS = [];
const EMPTY_CLAIMS = [];
const EMPTY_SUGGESTED = [];
const DEFAULT_META = {
    statementHash: null,
    status: 'idle',
    error: null,
    extractedAt: null,
    verifiedAt: null,
    lastProcessedAt: null,
};

/**
 * Convert camelCase meta.claimsSummary → snake_case used by UI components.
 */
function toSnakeSummary(s) {
    if (!s) return null;
    return {
        total_claims: s.totalClaims ?? 0,
        proven_claims: s.provenClaims ?? 0,
        partially_proven_claims: s.partiallyProvenClaims ?? 0,
        contradicted_claims: s.contradictedClaims ?? 0,
        unproven_claims: s.unprovenClaims ?? 0,
    };
}

/**
 * Derive summary from claims array when backend hasn't written claimsSummary.
 */
function summaryFromClaims(claims) {
    if (!claims?.length) return null;
    const s = {
        total_claims: claims.length,
        proven_claims: 0,
        partially_proven_claims: 0,
        contradicted_claims: 0,
        unproven_claims: 0
    };
    claims.forEach(c => {
        switch (c.status) {
            case 'proven':
                s.proven_claims++;
                break;
            case 'partially_proven':
                s.partially_proven_claims++;
                break;
            case 'contradicted':
                s.contradicted_claims++;
                break;
            default:
                s.unproven_claims++;
        }
    });
    return s;
}

/**
 * Build a snapshot object from the Y.Map data.
 */
function buildSnapshot(yReferenceMarks, referenceMarkId, fields) {
    const wantRefs = !fields || fields.includes('references');
    const wantClaims = !fields || fields.includes('claims');
    const wantSuggestions = !fields || fields.includes('suggestions');

    let references = EMPTY_REFS;
    let claims = EMPTY_CLAIMS;
    let meta = DEFAULT_META;
    let summary = null;
    let suggestedReferences = EMPTY_SUGGESTED;
    let contradictions = [];
    let extrinsicNotes = {};
    let evidenceScores = null;

    if (yReferenceMarks && referenceMarkId) {
        if (wantRefs) {
            references = getReferencesForMark(yReferenceMarks, referenceMarkId);
        }
        if (wantClaims) {
            const claimsData = getClaimsForMark(yReferenceMarks, referenceMarkId);
            claims = claimsData.claims;
            meta = claimsData.meta;
            summary = meta?.claimsSummary
                ? toSnakeSummary(meta.claimsSummary)
                : (claims.length > 0 ? summaryFromClaims(claims) : null);
            contradictions = meta?.contradictions ?? [];
            extrinsicNotes = meta?.extrinsicNotes ?? {};
        }
        if (wantSuggestions) {
            suggestedReferences = getSuggestedReferences(yReferenceMarks, referenceMarkId);
            // Read meta for suggestedReferencesStatus if claims weren't already requested
            if (!wantClaims) {
                const claimsData = getClaimsForMark(yReferenceMarks, referenceMarkId);
                meta = claimsData.meta;
            }
        }
        if (wantRefs) {
            // Evidence scores live on the reference objects themselves. Forecast-tagged
            // references are excluded here (MINSKY-16818 ticket 02) so AI-generated forecast
            // citations don't leak into claims-scoring consumers — the raw `references` list
            // above is untouched so the margin-rail "Added References" listing still shows them.
            const scores = {};
            let hasScores = false;
            excludeForecastReferences(references).forEach(ref => {
                if (ref.localId && ref.evidenceScore) {
                    scores[ref.localId] = ref.evidenceScore;
                    hasScores = true;
                }
            });
            evidenceScores = hasScores ? scores : {};
        }
    }

    // Derive loading states from meta
    const status = meta?.status ?? 'idle';
    const extractionLoading = status === 'extracting';
    const verificationLoading = status === 'verifying';
    const analysisLoading = status === 'loading' || extractionLoading || verificationLoading;

    const suggestedReferencesStatus = meta?.suggestedReferencesStatus ?? 'idle';
    const suggestionsLoading = suggestedReferencesStatus === 'pending' || suggestedReferencesStatus === 'analysing';
    const suggestedReferencesError = meta?.suggestedReferencesError ?? null;
    const proofInsightsLoading = extractionLoading || verificationLoading || suggestionsLoading;

    return {
        references,
        count: references.length,
        claims,
        meta,
        summary,
        suggestedReferences,
        contradictions,
        extrinsicNotes,
        evidenceScores: evidenceScores ?? {},
        analysisLoading,
        extractionLoading,
        verificationLoading,
        status,
        suggestedReferencesStatus,
        suggestionsLoading,
        suggestedReferencesError,
        proofInsightsLoading,
    };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {string|null|undefined} referenceMarkId
 * @param {{ fields?: ('references'|'claims'|'suggestions')[] }} [options]
 * @returns {Object} Snapshot of all requested Y.Map data for the mark
 */
export function useReferenceMarkData(referenceMarkId, options) {
    const yReferenceMarks = useYDocStore(state => state.yReferenceMarks);
    const fields = options?.fields ?? null;

    // Stable fields key for memoization
    const fieldsKey = fields ? fields.sort().join(',') : 'all';

    // Version counter — bumped by the observer, forces getSnapshot to rebuild
    const versionRef = useRef(0);
    const cacheRef = useRef(null);

    const subscribe = useCallback((onStoreChange) => {
        if (!yReferenceMarks || !referenceMarkId) {
            return () => {
            };
        }
        return subscribeToMark(yReferenceMarks, referenceMarkId, () => {
            versionRef.current += 1;
            onStoreChange();
        });
    }, [yReferenceMarks, referenceMarkId]);

    const getSnapshot = useCallback(() => {
        const version = versionRef.current;
        const cached = cacheRef.current;

        if (cached
            && cached.version === version
            && cached.fieldsKey === fieldsKey
            && cached.referenceMarkId === referenceMarkId
            && cached.yReferenceMarks === yReferenceMarks) {
            return cached.snapshot;
        }

        const snapshot = buildSnapshot(yReferenceMarks, referenceMarkId, fields);
        cacheRef.current = {version, fieldsKey, referenceMarkId, yReferenceMarks, snapshot};
        return snapshot;
    }, [yReferenceMarks, referenceMarkId, fields, fieldsKey]);

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export default useReferenceMarkData;

